import { useTranslation } from 'react-i18next'
import type { RoutePublic, Waypoint, WaypointKind } from '@xingroute/schema'
import type { EditableWaypoint } from '../lib/mergedWaypoints.js'
import type { DraftMediaChanges } from '../lib/routeEditSession.js'
import { Button } from './ui/Button.js'
import { useLocale } from '../locale/LocaleContext.js'
import { userTypeLabel } from './WaypointRow.js'
import { enumDisplay } from '../lib/enumDisplay.js'
import { formatSeasonalityForPreview } from './RouteSeasonalityBlock.js'
import {
  hasMetadataChanges,
  hasWaypointChanges,
  hasGpxChanges,
  hasMediaChanges,
} from '../lib/routeEditSession.js'
import { AuthorizedMediaImage } from './AuthorizedMediaImage.js'

// EditDiffPreview — structured 3-section diff preview modal.
// Replaces DiffPreviewModal for the unified edit-save flow.

export interface EditDiffPreviewProps {
  open: boolean
  route: RoutePublic
  draft: Record<string, unknown>
  serverWaypoints: readonly Waypoint[]
  draftWaypoints: EditableWaypoint[] | null
  draftGpxFile: File | null
  draftTrackReversed: boolean
  mediaChanges: DraftMediaChanges
  serverMedia: readonly { id: string; blob_url: string; thumb_url?: string }[]
  mediaToken?: string | null
  statusAction?: 'publish' | 'request'
  onClose: () => void
  onConfirm: () => void | Promise<void>
  confirmDisabled: boolean
}

export function EditDiffPreview({
  open,
  route,
  draft,
  serverWaypoints,
  draftWaypoints,
  draftGpxFile,
  draftTrackReversed,
  mediaChanges,
  serverMedia,
  mediaToken,
  statusAction,
  onClose,
  onConfirm,
  confirmDisabled,
}: EditDiffPreviewProps) {
  const { t } = useTranslation('common')
  const { locale } = useLocale()
  if (!open) return null

  const metaChanged = hasMetadataChanges(draft, route)
  const wpChanged = hasWaypointChanges(draftWaypoints, serverWaypoints)
  const gpxChanged = hasGpxChanges(draftGpxFile, draftTrackReversed)
  const mediaChanged = hasMediaChanges(mediaChanges)
  const statusChanged =
    (statusAction === 'publish' && route.status !== 'published') ||
    (statusAction === 'request' && route.status === 'draft')
  const anyChanged = metaChanged || wpChanged || gpxChanged || mediaChanged || statusChanged

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-[var(--xr-overlay-bg)] p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="edit-diff-preview-title"
      onClick={onClose}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose()
      }}
      data-testid="edit-diff-preview"
    >
      <div
        className="flex max-h-[80dvh] w-full max-w-2xl flex-col overflow-hidden rounded-[var(--xr-radius-lg)] border border-[var(--xr-border-default)] bg-[var(--xr-bg-surface)] shadow-[var(--xr-shadow-md)]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex flex-none items-center justify-between gap-3 border-b border-[var(--xr-border-default)] px-4 py-3">
          <h2
            id="edit-diff-preview-title"
            className="text-base font-semibold text-[var(--xr-text-primary)]"
          >
            {locale === 'zh' ? '保存前预览' : 'Review changes'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={locale === 'zh' ? '关闭' : 'Close'}
            className="rounded px-2 py-1 text-sm text-[var(--xr-text-muted)] hover:bg-[var(--xr-bg-surface-elev)]"
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4 text-sm">
          {!anyChanged && (
            <p className="text-[var(--xr-text-muted)]">
              {locale === 'zh' ? '无变更' : 'No changes'}
            </p>
          )}

          {/* Section 1: Metadata */}
          {metaChanged && (
            <section>
              <h3 className="mb-1 font-medium text-[var(--xr-text-primary)]">
                {locale === 'zh' ? '基本信息' : 'Basic info'}
              </h3>
              <MetadataDiff route={route} draft={draft} locale={locale} />
            </section>
          )}

          {/* Section 2: Track & Waypoints */}
          {(gpxChanged || wpChanged) && (
            <section>
              <h3 className="mb-1 font-medium text-[var(--xr-text-primary)]">
                {locale === 'zh' ? '轨迹与路点' : 'Track & waypoints'}
              </h3>
              {gpxChanged && (
                <p className="text-[var(--xr-text-secondary)]">
                  GPX:{' '}
                  {draftGpxFile
                    ? locale === 'zh'
                      ? '已替换'
                      : 'Replaced'
                    : draftTrackReversed
                      ? locale === 'zh'
                        ? '已反向'
                        : 'Reversed'
                      : ''}
                </p>
              )}
              {wpChanged && (
                <WaypointDiff
                  server={serverWaypoints}
                  draft={draftWaypoints!}
                  locale={locale}
                  t={t}
                />
              )}
            </section>
          )}

          {/* Section 3: Media */}
          {mediaChanged && (
            <section>
              <h3 className="mb-1 font-medium text-[var(--xr-text-primary)]">
                {locale === 'zh' ? '图片' : 'Images'}
              </h3>
              {mediaChanges.added.length > 0 && (
                <div className="mb-2">
                  <span className="text-[var(--xr-success)]">
                    + {mediaChanges.added.length} {locale === 'zh' ? '张新增' : 'added'}
                  </span>
                  <div className="mt-1 flex gap-1 flex-wrap">
                    {mediaChanges.added.map((item) =>
                      item.file.type.startsWith('video/') ? (
                        <div
                          key={item.id}
                          className="flex h-12 w-12 items-center justify-center rounded border border-[var(--xr-success)]/40 bg-[var(--xr-media-bg)] text-[10px] text-[var(--xr-text-muted)]"
                        >
                          {locale === 'zh' ? '视频' : 'Video'}
                        </div>
                      ) : (
                        <img
                          key={item.id}
                          src={item.previewUrl}
                          alt=""
                          className="h-12 w-12 rounded object-cover border border-[var(--xr-success)]/40"
                        />
                      ),
                    )}
                  </div>
                </div>
              )}
              {mediaChanges.removed.length > 0 && (
                <div>
                  <span className="text-[var(--xr-danger)]">
                    - {mediaChanges.removed.length} {locale === 'zh' ? '张删除' : 'removed'}
                  </span>
                  <div className="mt-1 flex gap-1 flex-wrap">
                    {mediaChanges.removed.map((id) => {
                      const m = serverMedia.find((s) => s.id === id)
                      return m ? (
                        <AuthorizedMediaImage
                          key={id}
                          source={m.thumb_url ?? m.blob_url}
                          token={mediaToken}
                          alt=""
                          className="h-12 w-12 rounded object-cover border border-[var(--xr-danger)]/40 opacity-60"
                          loading="lazy"
                        />
                      ) : null
                    })}
                  </div>
                </div>
              )}
            </section>
          )}

          {statusChanged && (
            <section>
              <h3 className="mb-1 font-medium text-[var(--xr-text-primary)]">
                {locale === 'zh' ? '发布状态' : 'Publish status'}
              </h3>
              <p className="text-xs text-[var(--xr-text-secondary)]">
                {statusAction === 'request'
                  ? locale === 'zh'
                    ? '将提交线路审核。'
                    : 'Submit this route for review.'
                  : locale === 'zh'
                    ? '将提交并发布线路。'
                    : 'Submit and publish this route.'}
              </p>
            </section>
          )}
        </div>

        {/* Footer */}
        <div className="flex flex-none items-center justify-end gap-2 border-t border-[var(--xr-border-default)] px-4 py-3">
          <Button variant="secondary" size="sm" onClick={onClose}>
            {t('cancel')}
          </Button>
          <Button
            size="sm"
            onClick={onConfirm}
            disabled={confirmDisabled || !anyChanged}
            data-testid="edit-diff-confirm"
          >
            {locale === 'zh' ? '确认提交' : 'Confirm'}
          </Button>
        </div>
      </div>
    </div>
  )
}

// --- Sub-components ---

function MetadataDiff({
  route,
  draft,
  locale,
}: {
  route: RoutePublic
  draft: Record<string, unknown>
  locale: string
}) {
  const { t } = useTranslation('common')
  const fields: { key: string; label: string }[] = [
    { key: 'title', label: locale === 'zh' ? '标题' : 'Title' },
    { key: 'summary', label: locale === 'zh' ? '描述' : 'Description' },
    { key: 'difficulty', label: locale === 'zh' ? '难度' : 'Difficulty' },
    { key: 'route_type', label: locale === 'zh' ? '线路类型' : 'Route type' },
  ]

  const enumFields = new Set(['difficulty', 'route_type'])
  function displayValue(key: string, value: unknown): string {
    if (!value) return ''
    const raw = String(value)
    if (key === 'difficulty') return enumDisplay('difficulty', raw, t).label
    if (key === 'route_type') return enumDisplay('route_type', raw, t).label
    return raw.slice(0, 50)
  }

  const changed = fields.filter(
    (f) =>
      JSON.stringify(draft[f.key] ?? null) !==
      JSON.stringify((route as Record<string, unknown>)[f.key] ?? null),
  )

  // Array/object fields
  const actChanged =
    JSON.stringify(draft.activities ?? null) !== JSON.stringify(route.activities ?? null)
  const regionChanged =
    JSON.stringify(draft.region ?? null) !== JSON.stringify(route.region ?? null)
  const seasonChanged =
    JSON.stringify(draft.seasonality ?? null) !== JSON.stringify(route.seasonality ?? null)

  return (
    <ul className="space-y-1 text-xs text-[var(--xr-text-secondary)]">
      {changed.map((f) => (
        <li key={f.key}>
          <span className="font-medium">{f.label}:</span>{' '}
          <span className="line-through text-[var(--xr-danger)]">
            {enumFields.has(f.key)
              ? displayValue(f.key, (route as Record<string, unknown>)[f.key])
              : String((route as Record<string, unknown>)[f.key] ?? '').slice(0, 50)}
          </span>{' '}
          →{' '}
          <span className="text-[var(--xr-success)]">
            {enumFields.has(f.key)
              ? displayValue(f.key, draft[f.key])
              : String(draft[f.key] ?? '').slice(0, 50)}
          </span>
        </li>
      ))}
      {actChanged && (
        <li>
          <span className="font-medium">{locale === 'zh' ? '活动' : 'Activities'}:</span>{' '}
          {String(route.activities?.join(', '))} →{' '}
          {String((draft.activities as string[])?.join(', '))}
        </li>
      )}
      {regionChanged && (
        <li>
          <span className="font-medium">{locale === 'zh' ? '区域' : 'Region'}:</span>{' '}
          {locale === 'zh' ? '已修改' : 'Modified'}
        </li>
      )}
      {seasonChanged && (
        <li>
          <span className="font-medium">{t('diffPreview.seasonality')}:</span>{' '}
          <span className="line-through text-[var(--xr-danger)]">
            {formatSeasonalityForPreview(route.seasonality) ||
              (locale === 'zh' ? '未设置' : 'Not set')}
          </span>{' '}
          →{' '}
          <span className="text-[var(--xr-success)]">
            {formatSeasonalityForPreview(draft.seasonality as RoutePublic['seasonality']) ||
              (locale === 'zh' ? '未设置' : 'Not set')}
          </span>
        </li>
      )}
    </ul>
  )
}

function WaypointDiff({
  server,
  draft,
  locale,
  t,
}: {
  server: readonly Waypoint[]
  draft: readonly EditableWaypoint[]
  locale: string
  t: (key: string) => string
}) {
  const serverUser = server.filter((w) => !w.auto)
  const draftUser = draft.filter((w) => !w.auto)
  const serverIds = new Set(serverUser.map((w) => w.id))
  const draftIds = new Set(draftUser.map((w) => w.id))

  const added = draftUser.filter((w) => !serverIds.has(w.id))
  const removed = serverUser.filter((w) => !draftIds.has(w.id))
  const modified = draftUser.filter((w) => {
    if (!serverIds.has(w.id)) return false
    const s = serverUser.find((sw) => sw.id === w.id)
    return s && (s.kind !== w.kind || s.desc !== w.desc)
  })

  return (
    <ul className="space-y-0.5 text-xs text-[var(--xr-text-secondary)]">
      {added.map((w) => (
        <li key={w.id} className="text-[var(--xr-success)]">
          + {userTypeLabel(w.kind as WaypointKind | undefined, w.name, t)}: {w.desc ?? ''}
        </li>
      ))}
      {removed.map((w) => (
        <li key={w.id} className="text-[var(--xr-danger)]">
          - {userTypeLabel(w.kind as WaypointKind | undefined, w.name, t)}: {w.desc ?? ''}
        </li>
      ))}
      {modified.map((w) => (
        <li key={w.id} className="text-[var(--xr-warning)]">
          ~ {userTypeLabel(w.kind as WaypointKind | undefined, w.name, t)}: {w.desc ?? ''}
        </li>
      ))}
    </ul>
  )
}
